/**
 * Judgment Scoring
 * ================
 *
 * The math behind the taxonomy. Everything here is a pure function over plain
 * numbers/arrays so it is trivially unit-testable and runs identically on the
 * client, in an Edge Function, or inside a Postgres RPC re-implementation.
 *
 * The scoring rests on four ideas:
 *
 *  1. Proper scoring rules (Brier, logarithmic). A forecaster is rewarded for
 *     stating probabilities that match reality and cannot game the score by
 *     shading toward 0/1 or 0.5.
 *
 *  2. The Murphy decomposition of the Brier score into
 *     `reliability − resolution + uncertainty`. This is what lets us say that
 *     *calibration* (reliability) and *discrimination* (resolution) are
 *     different skills, and score them separately.
 *
 *  3. The non-consensus edge. A forecaster's log score is compared against the
 *     crowd/consensus forecast for the same question. Being right is cheap when
 *     everyone agreed; the edge measures how much information a person added
 *     *beyond* the consensus, so being right against a confident crowd — the
 *     mark of real, entrepreneurial judgment — dominates.
 *
 *  4. Empirical-Bayes shrinkage. No score from three calls is trustworthy.
 *     Every raw score is shrunk toward the population mean by its effective
 *     sample size, so confidence in a person's number grows only as their
 *     resolved track record does. This is the "common sense" guardrail.
 *
 * Design spec: docs/9-exploration/judgment-analytics/DESIGN.md
 */

import type { JudgmentTier } from '../../types/decisionIntelligence';

/** Clamp x into [lo, hi]. */
export function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

/**
 * Direction-aware belief probability of the positive outcome from a
 * recommendation + confidence. Mirrors the SQL `judgment_belief_probability`
 * (migration 606): a confident `stop` predicts *non*-success, not success.
 *   proceed → confidence; stop/pause → 1 − confidence; modify → 0.5.
 * `confidence01` is confidence normalised to [0,1].
 */
export function beliefProbability(
  recommendation: 'proceed' | 'pause' | 'stop' | 'modify',
  confidence01: number,
): number {
  const c = clamp(confidence01, 0, 1);
  if (recommendation === 'proceed') return c;
  if (recommendation === 'modify') return 0.5;
  return 1 - c; // pause / stop
}

/** A single resolved probabilistic forecast: a stated probability and its binary outcome. */
export interface ResolvedForecast {
  /** Stated probability of the positive outcome, in [0, 1]. */
  p: number;
  /** Realized outcome: 1 = happened, 0 = did not. */
  outcome: 0 | 1;
}

// ============================================================================
// Proper scoring rules
// ============================================================================

/**
 * Brier score for one probabilistic forecast: (p − outcome)². Range [0, 1],
 * lower is better. A proper scoring rule.
 */
export function brierScore(p: number, outcome: 0 | 1): number {
  const q = clamp(p, 0, 1);
  return (q - outcome) ** 2;
}

/** Mean Brier score over a set of resolved forecasts. Returns null if empty. */
export function meanBrier(forecasts: ResolvedForecast[]): number | null {
  if (forecasts.length === 0) return null;
  const sum = forecasts.reduce((acc, f) => acc + brierScore(f.p, f.outcome), 0);
  return sum / forecasts.length;
}

/**
 * Logarithmic score for one forecast: −ln(p) if it happened, −ln(1−p) if not.
 * Range [0, −ln(EPS)], lower is better. Probabilities are clamped away from
 * 0/1 by `eps` so a confident miss is heavily — but finitely — penalized.
 */
const LOG_EPS = 1e-6;
export function logScore(p: number, outcome: 0 | 1, eps: number = LOG_EPS): number {
  const q = clamp(p, eps, 1 - eps);
  return outcome === 1 ? -Math.log(q) : -Math.log(1 - q);
}

// ============================================================================
// Calibration vs resolution — the Murphy decomposition
// ============================================================================

export interface BrierDecomposition {
  /** Mean Brier score = reliability − resolution + uncertainty. */
  brier: number;
  /** Reliability (calibration error): lower is better. 0 = perfectly calibrated. */
  reliability: number;
  /** Resolution (discrimination): higher is better. 0 = never beats the base rate. */
  resolution: number;
  /** Uncertainty = baseRate·(1−baseRate): the irreducible difficulty of the set. */
  uncertainty: number;
  /** Observed base rate of the positive outcome across the set. */
  baseRate: number;
  /** Number of forecasts scored. */
  n: number;
}

/**
 * Decompose a set of probabilistic forecasts into reliability, resolution and
 * uncertainty (Murphy, 1973). Forecasts are grouped into `bins` equal-width
 * probability buckets; within each bucket we compare the mean forecast to the
 * observed frequency.
 *
 *   reliability = (1/N) Σ_k n_k (p̄_k − ō_k)²     — calibration error, lower better
 *   resolution  = (1/N) Σ_k n_k (ō_k − ō)²       — discrimination, higher better
 *   uncertainty = ō(1 − ō)                        — base-rate difficulty
 *   brier       = reliability − resolution + uncertainty
 *
 * Returns null for an empty set.
 */
export function brierDecomposition(
  forecasts: ResolvedForecast[],
  bins = 10,
): BrierDecomposition | null {
  const n = forecasts.length;
  if (n === 0) return null;

  const baseRate = forecasts.reduce((acc, f) => acc + f.outcome, 0) / n;
  const uncertainty = baseRate * (1 - baseRate);

  // Assign each forecast to a bin index in [0, bins-1].
  const buckets: ResolvedForecast[][] = Array.from({ length: bins }, () => []);
  for (const f of forecasts) {
    const idx = clamp(Math.floor(clamp(f.p, 0, 1) * bins), 0, bins - 1);
    buckets[idx].push(f);
  }

  let reliability = 0;
  let resolution = 0;
  for (const bucket of buckets) {
    const nk = bucket.length;
    if (nk === 0) continue;
    const meanP = bucket.reduce((a, f) => a + clamp(f.p, 0, 1), 0) / nk;
    const obs = bucket.reduce((a, f) => a + f.outcome, 0) / nk;
    reliability += nk * (meanP - obs) ** 2;
    resolution += nk * (obs - baseRate) ** 2;
  }
  reliability /= n;
  resolution /= n;

  return {
    brier: reliability - resolution + uncertainty,
    reliability,
    resolution,
    uncertainty,
    baseRate,
    n,
  };
}

/**
 * Calibration score in [0, 1], higher = better, consistent with the existing
 * `judgment_profiles.calibration_score` definition (1 − mean Brier). Prefer the
 * reliability term from `brierDecomposition` when you specifically want
 * calibration *isolated* from discrimination.
 */
export function calibrationScore(forecasts: ResolvedForecast[]): number | null {
  const mb = meanBrier(forecasts);
  return mb === null ? null : clamp(1 - mb, 0, 1);
}

// ============================================================================
// The non-consensus edge
// ============================================================================

/** One resolved forecast paired with the crowd/consensus forecast for the same question. */
export interface ForecastVsCrowd {
  /** The individual's stated probability, in [0, 1]. */
  individual: number;
  /** The consensus/crowd probability for the same question, in [0, 1]. */
  crowd: number;
  /** Realized outcome. */
  outcome: 0 | 1;
}

/**
 * Information gained over the consensus on a single question, in nats:
 *   edge = logScore(crowd) − logScore(individual)
 * Positive when the individual predicted the truth better than the crowd did.
 *
 * This is naturally surprisal-weighted: when the crowd was confidently wrong
 * (logScore(crowd) large) and the individual was right, the edge is large — the
 * exact signature of valuable non-consensus judgment. When everyone already
 * agreed and was right, the edge is near zero — being right *with* the crowd is
 * correctly treated as cheap.
 */
export function consensusEdge(f: ForecastVsCrowd, eps: number = LOG_EPS): number {
  return logScore(f.crowd, f.outcome, eps) - logScore(f.individual, f.outcome, eps);
}

/** Mean non-consensus edge over the questions a person and the crowd both forecast. */
export function meanConsensusEdge(forecasts: ForecastVsCrowd[]): number | null {
  if (forecasts.length === 0) return null;
  return forecasts.reduce((acc, f) => acc + consensusEdge(f), 0) / forecasts.length;
}

/**
 * How surprising the realized truth was under the crowd's belief, in nats:
 * −ln(crowd) if it happened, −ln(1−crowd) if not. A weight you can apply when
 * you want to emphasize calls where the consensus was blindsided.
 */
export function crowdSurprisal(crowd: number, outcome: 0 | 1, eps: number = LOG_EPS): number {
  return logScore(crowd, outcome, eps);
}

/**
 * Contrarian correctness: among the calls where the individual meaningfully
 * *disagreed* with the crowd (|individual − crowd| ≥ `divergence`), the share on
 * which the individual was closer to the truth than the crowd. Returns the count
 * of divergent calls alongside the rate so callers can gate on sample size.
 */
export function contrarianCorrectness(
  forecasts: ForecastVsCrowd[],
  divergence = 0.2,
): { rate: number | null; divergentCount: number } {
  const divergent = forecasts.filter((f) => Math.abs(f.individual - f.crowd) >= divergence);
  if (divergent.length === 0) return { rate: null, divergentCount: 0 };
  const right = divergent.filter(
    (f) => Math.abs(f.individual - f.outcome) < Math.abs(f.crowd - f.outcome),
  ).length;
  return { rate: right / divergent.length, divergentCount: divergent.length };
}

// ============================================================================
// Similarity & distinctiveness
// ============================================================================

/**
 * Cosine similarity between two equal-length vectors, in [−1, 1]. Returns null
 * if either vector has zero magnitude (no basis for comparison).
 */
export function cosineSimilarity(a: number[], b: number[]): number | null {
  if (a.length !== b.length || a.length === 0) return null;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  if (na === 0 || nb === 0) return null;
  return clamp(dot / (Math.sqrt(na) * Math.sqrt(nb)), -1, 1);
}

/** A person's stance vector: probability per shared question id. */
export type StanceVector = Record<string, number>;

/**
 * Similarity between two people over the questions they *both* forecast, mapping
 * each stance from [0,1] to [−1,1] first so that "0.9 vs 0.9" reads as strong
 * agreement and "0.9 vs 0.1" as strong disagreement (rather than both looking
 * positive). Returns null if they share fewer than `minShared` questions.
 */
export function stanceSimilarity(
  a: StanceVector,
  b: StanceVector,
  minShared = 3,
): number | null {
  const shared = Object.keys(a).filter((k) => k in b);
  if (shared.length < minShared) return null;
  const va = shared.map((k) => clamp(a[k], 0, 1) * 2 - 1);
  const vb = shared.map((k) => clamp(b[k], 0, 1) * 2 - 1);
  return cosineSimilarity(va, vb);
}

/**
 * Distinctiveness of one person against a set of peers: 1 − mean similarity,
 * in [0, 2]. Higher = more differentiated from the group. Peers with too few
 * shared questions are skipped; returns null if no peer is comparable.
 */
export function distinctiveness(
  person: StanceVector,
  peers: StanceVector[],
  minShared = 3,
): number | null {
  const sims = peers
    .map((peer) => stanceSimilarity(person, peer, minShared))
    .filter((s): s is number => s !== null);
  if (sims.length === 0) return null;
  const meanSim = sims.reduce((a, s) => a + s, 0) / sims.length;
  return 1 - meanSim;
}

// ============================================================================
// Empirical-Bayes shrinkage — the small-sample guardrail
// ============================================================================

/**
 * Shrink a proportion (k successes of n) toward a prior mean using
 * Beta-Binomial pseudo-counts: (k + κ·m) / (n + κ). With n=0 this returns the
 * prior mean; as n grows it converges to the raw rate. `priorStrength` (κ) is
 * the number of pseudo-observations — how many real calls it takes to move the
 * estimate halfway off the prior.
 */
export function shrinkProportion(
  k: number,
  n: number,
  priorMean: number,
  priorStrength = 5,
): number {
  return (k + priorStrength * priorMean) / (n + priorStrength);
}

/**
 * Shrink a continuous mean score toward a prior by effective sample size:
 * (n·raw + κ·prior) / (n + κ). Use for calibration/edge scores where you have a
 * mean over n resolved calls rather than a success count.
 */
export function shrinkScore(
  rawScore: number,
  n: number,
  priorMean: number,
  priorStrength = 5,
): number {
  if (n <= 0) return priorMean;
  return (n * rawScore + priorStrength * priorMean) / (n + priorStrength);
}

/**
 * Confidence weight in [0, 1) from sample size: n / (n + κ). The fraction of the
 * way a person's raw score is trusted over the prior. Use it to fade UI emphasis
 * and to weight a person's vote in an aggregate trust ranking.
 */
export function confidenceWeight(n: number, priorStrength = 5): number {
  if (n <= 0) return 0;
  return n / (n + priorStrength);
}

// ============================================================================
// Insight archetypes — the selective-and-right sweet spot
// ============================================================================
//
// Raw distinctiveness is a trap: someone who disagrees with everyone about
// everything is noise, not signal, and no one wants to staff a team with
// reflexive contrarians. The desired profile is the opposite in shape — a good
// teammate whose judgment sits inside a tight circle around consensus for the
// ~90% of calls where that's correct, but who has a *localized, correct* edge in
// specific pockets we can identify and go employ. These functions separate that
// person from the contrarian and from the reliable-but-no-edge voice.

export type InsightArchetype =
  | 'insightful_teammate' // Aligned most of the time, selectively & correctly divergent. The sweet spot.
  | 'reliable_consensus' //  Aligned; no demonstrated unique edge. Solid, not differentiated.
  | 'maverick' //            Frequently divergent and right, but low baseline alignment. High-value outsider; harder to integrate.
  | 'contrarian_noise' //    Frequently divergent and not right. Down-weight.
  | 'insufficient_data';

export interface InsightProfile {
  n: number;
  /** Share of calls within the consensus band. ~0.85–0.9 is the healthy teammate norm. */
  baselineAlignment: number;
  /** Share of calls where the person meaningfully diverged from consensus. */
  divergentShare: number;
  divergentCount: number;
  /** Among divergent calls, share where the person was closer to the truth than the crowd. */
  divergentAccuracy: number | null;
  /** Shrunk mean non-consensus edge over the divergent calls only, in nats. The employable edge. */
  insightEdge: number | null;
  /** Confidence in the edge from the size of the divergent sample, in [0,1). */
  confidence: number;
  archetype: InsightArchetype;
}

export interface InsightOptions {
  /** |individual − crowd| ≥ this counts as a meaningful divergence. */
  divergenceThreshold?: number;
  /** Minimum total resolved calls before any archetype other than insufficient_data. */
  minResolved?: number;
  /** Minimum divergent calls before an edge can be claimed. */
  minDivergent?: number;
  /** Baseline alignment at/above this reads as a well-aligned teammate. */
  teammateAlignmentFloor?: number;
  /** Divergent accuracy at/above this counts the divergences as genuinely insightful. */
  goodDivergentAccuracy?: number;
  /** Shrinkage strength for the divergent-edge mean (pseudo-observations toward 0). */
  edgePriorStrength?: number;
}

/**
 * Build a person's insight profile from their resolved calls (each paired with
 * the crowd forecast and outcome). Classifies into an archetype that a leader
 * can act on: seek out the `insightful_teammate`, validate the `maverick`, lean
 * on the `reliable_consensus` for a steady read, and down-weight
 * `contrarian_noise`.
 */
export function insightProfile(
  forecasts: ForecastVsCrowd[],
  opts: InsightOptions = {},
): InsightProfile {
  const {
    divergenceThreshold = 0.2,
    minResolved = 10,
    minDivergent = 4,
    teammateAlignmentFloor = 0.6,
    goodDivergentAccuracy = 0.6,
    edgePriorStrength = 4,
  } = opts;

  const n = forecasts.length;
  const { rate: divergentAccuracy, divergentCount } = contrarianCorrectness(
    forecasts,
    divergenceThreshold,
  );
  const baselineAlignment = n === 0 ? 0 : (n - divergentCount) / n;
  const divergentShare = n === 0 ? 0 : divergentCount / n;

  const divergent = forecasts.filter(
    (f) => Math.abs(f.individual - f.crowd) >= divergenceThreshold,
  );
  const rawEdge = meanConsensusEdge(divergent);
  const insightEdge =
    rawEdge === null ? null : shrinkScore(rawEdge, divergentCount, 0, edgePriorStrength);
  const confidence = confidenceWeight(divergentCount, edgePriorStrength);

  let archetype: InsightArchetype;
  if (n < minResolved) {
    archetype = 'insufficient_data';
  } else {
    const hasProvenEdge =
      divergentCount >= minDivergent &&
      divergentAccuracy !== null &&
      divergentAccuracy >= goodDivergentAccuracy &&
      (insightEdge ?? 0) > 0;
    const aligned = baselineAlignment >= teammateAlignmentFloor;
    if (aligned) {
      archetype = hasProvenEdge ? 'insightful_teammate' : 'reliable_consensus';
    } else {
      archetype = hasProvenEdge ? 'maverick' : 'contrarian_noise';
    }
  }

  return {
    n,
    baselineAlignment,
    divergentShare,
    divergentCount,
    divergentAccuracy,
    insightEdge,
    confidence,
    archetype,
  };
}

/**
 * A single ranking scalar for "whose unique insight should we go employ?".
 * Rewards a proven, confident edge, and — all else equal — ranks a well-aligned
 * teammate above an equally-insightful low-alignment maverick (they are easier
 * to integrate and trust). `contrarian_noise` and thin records score 0, so the
 * ranking never surfaces a reflexive contrarian.
 */
export function insightEmployability(profile: InsightProfile): number {
  if (profile.archetype === 'contrarian_noise' || profile.archetype === 'insufficient_data') {
    return 0;
  }
  const edge = Math.max(0, profile.insightEdge ?? 0);
  const teammateFactor = 0.5 + 0.5 * clamp(profile.baselineAlignment, 0, 1);
  return edge * profile.confidence * teammateFactor;
}

// ============================================================================
// Stakes & confidence deltas — belief / confidence / stakes at work
// ============================================================================

/** One person's position on a choice, with how sure and how much they care. */
export interface PreferenceSignal {
  /** The option this person backs (belief/direction). */
  option: string;
  /** How sure they are it's the right choice, in [0,1]. Optional; defaults to 1. */
  confidence?: number;
  /** How much the outcome matters to them, in [0,1]. The intensity that breaks ties. */
  stakes: number;
}

export interface PreferenceResolution {
  /** The winning option, or null if there were no signals. */
  winner: string | null;
  /** Per-option total weight, weighted by stakes (× confidence when present). */
  tally: Record<string, number>;
  /** Winner's share of total weight, in [0,1]. */
  margin: number;
}

/**
 * Resolve a choice by intensity — the Chick-fil-A (stakes 4) vs Whataburger
 * (stakes 9) case. Each person's vote is weighted by their stakes, multiplied by
 * their confidence when supplied. All else equal, the side that cares more wins.
 * A later phase multiplies in each voter's track-record weight so that stakes are
 * heard *and* earned.
 */
export function resolvePreference(signals: PreferenceSignal[]): PreferenceResolution {
  const tally: Record<string, number> = {};
  let total = 0;
  for (const s of signals) {
    const weight = clamp(s.stakes, 0, 1) * clamp(s.confidence ?? 1, 0, 1);
    tally[s.option] = (tally[s.option] ?? 0) + weight;
    total += weight;
  }
  let winner: string | null = null;
  let best = -Infinity;
  for (const [option, weight] of Object.entries(tally)) {
    if (weight > best) {
      best = weight;
      winner = option;
    }
  }
  return { winner, tally, margin: total > 0 ? best / total : 0 };
}

/** Signed gap between an individual value and the group/peer mean. */
export function delta(individual: number, peerMean: number): number {
  return individual - peerMean;
}

/** Mean signed confidence gap vs peers across calls — a systematic over/under-confidence read. */
export function meanConfidenceDelta(
  ownConfidences: number[],
  peerMeanConfidences: number[],
): number | null {
  const k = Math.min(ownConfidences.length, peerMeanConfidences.length);
  if (k === 0) return null;
  let sum = 0;
  for (let i = 0; i < k; i++) sum += ownConfidences[i] - peerMeanConfidences[i];
  return sum / k;
}

/**
 * Overconfidence: mean stated confidence minus realized accuracy. Positive =
 * more confident than correct; negative = underconfident. The classic
 * calibration gap, in [−1, 1].
 */
export function overconfidence(meanConfidence: number, accuracy: number): number {
  return clamp(meanConfidence, 0, 1) - clamp(accuracy, 0, 1);
}

/**
 * Relative overconfidence: how much of a person's confidence edge over peers is
 * *unearned* — the confidence gap minus the accuracy gap. Positive means they
 * are louder than their results justify relative to the group; ~0 means their
 * extra confidence is backed by extra accuracy.
 */
export function relativeOverconfidence(
  confidenceDelta: number,
  accuracyDelta: number,
): number {
  return confidenceDelta - accuracyDelta;
}

/**
 * Stakes inflation: a person's mean stakes minus the peer mean. Persistently
 * positive is the "cries wolf / everything is a 9" pattern that erodes the value
 * of the stakes signal; near zero means they reserve high stakes for what
 * actually matters to them.
 */
export function stakesInflation(ownMeanStakes: number, peerMeanStakes: number): number {
  return ownMeanStakes - peerMeanStakes;
}

// ============================================================================
// Tiering
// ============================================================================

export interface TierInput {
  /** Shrunk calibration score in [0,1], or null if unknown. */
  calibration: number | null;
  /** Resolution term from the decomposition, or null if unknown. */
  resolution: number | null;
  /** Number of resolved calls behind the scores. */
  resolvedCount: number;
}

/**
 * Map scores to a display tier, reusing the existing `JudgmentTier` union.
 * Below `minResolved` resolved calls we always return `insufficient_data` — a
 * hard refusal to rank anyone on a thin record, regardless of how good the raw
 * numbers look. `superforecaster` additionally requires genuine discrimination
 * (resolution), not just calibration, so a hedger who always predicts the base
 * rate cannot reach the top tier.
 */
export function classifyTier(input: TierInput, minResolved = 10): JudgmentTier {
  const { calibration, resolution, resolvedCount } = input;
  if (resolvedCount < minResolved || calibration === null) return 'insufficient_data';
  if (calibration >= 0.85 && (resolution ?? 0) >= 0.04) return 'superforecaster';
  if (calibration >= 0.7) return 'strong';
  return 'developing';
}
